import React, { useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  CONTACT_FIELDS,
  COMPANY_FIELDS,
  VEHICLE_FIELDS,
  COVER_FIELDS,
  COVER_OPTIONS,
  CUSTOMER_TYPES,
  T,
  optionLabel,
  isPolishPhone
} from './motorForm';
import { submitLead } from './submitLead';

// Port usppublicsite/src/components/sections/MotorQuoteForm.tsx. Zamiast
// /api/lead dane idą na endpoint TVP jako jedna wiadomość tekstowa.
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const YEAR_RE = /^(19|20)\d{2}$/;
const INPUT_TYPE = {
  text: 'text',
  email: 'email',
  phone: 'tel',
  date: 'date',
  year: 'text'
};
const INPUT_MODE = { phone: 'tel', email: 'email', year: 'numeric' };

function fieldError(def, raw) {
  const v = (raw || '').trim();
  if (!v) return def.required ? 'errRequired' : null;
  switch (def.kind) {
    case 'email':
      return EMAIL_RE.test(v) ? null : 'errEmail';
    case 'phone':
      return isPolishPhone(v) ? null : 'errPhone';
    case 'year':
      return YEAR_RE.test(v) ? null : 'errYear';
    default:
      return null;
  }
}

// SubmitFormScript (app Luxmed) wkleja context.message do maila HTML bez
// escapowania — stąd <br /> zamiast \n i pogrubienia.
const line = (label, value) => (value ? `<b>${label}:</b> ${value}` : null);

function buildMessage({ values, customerType, cover, val }) {
  const vehicle = VEHICLE_FIELDS.map((f) =>
    line(
      f.label,
      f.kind === 'select' ? optionLabel(f.options, val(f.key)) : val(f.key)
    )
  );
  const coverList = COVER_OPTIONS.filter((o) => cover[o.value]).map(
    (o) => o.label
  );
  const company =
    customerType === 'company'
      ? COMPANY_FIELDS.map((f) => line(f.label, val(f.key)))
      : [];

  return [
    '<b>Zapytanie: Ubezpieczenie Twojego Auta — Pakiety Dealerskie</b>',
    '',
    line('Klient', optionLabel(CUSTOMER_TYPES, customerType)),
    ...company,
    '',
    '<u>Pojazd</u>',
    ...vehicle,
    '',
    '<u>Zakres ochrony</u>',
    line('Interesuje', coverList.join(', ')),
    ...COVER_FIELDS.map((f) => line(f.label, val(f.key))),
    '',
    line('Dodatkowe informacje', values.notes)
  ]
    .filter((l) => l !== null)
    .join('<br />');
}

const MotorQuoteForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [values, setValues] = useState({});
  const [customerType, setCustomerType] = useState(undefined);
  const [cover, setCover] = useState({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const okRef = useRef(null);

  // Po wysłaniu wysoki formularz znika i zostaje niska karta — bez tego
  // okno zostaje na dawnej pozycji scrolla, poniżej podziękowania.
  useEffect(() => {
    if (sent && okRef.current) {
      okRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [sent]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const set = (key) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [key]: value }));
  };
  const val = (key) => (values[key] || '').trim();

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    [...CONTACT_FIELDS, ...VEHICLE_FIELDS, ...COVER_FIELDS].forEach((def) => {
      const err = fieldError(def, values[def.key]);
      if (err) next[def.key] = err;
    });
    if (!customerType) next.customerType = 'errChoice';
    if (!COVER_OPTIONS.some((o) => cover[o.value])) next.cover = 'errCover';
    if (!consent) next.consent = 'errRequired';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitLead(executeRecaptcha, {
        fullName: val('fullName'),
        phone: val('phone'),
        email: val('email'),
        message: buildMessage({ values, customerType, cover, val }),
        subject: 'Zgłoszenie - Ubezpieczenie Twojego Auta - Pakiety Dealerskie'
      });
      setSent(true);
    } catch (err) {
      setSubmitError(T.errGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const requiredMark = (
    <>
      <span className="mq-req" aria-hidden>
        {' '}
        *
      </span>
      <span className="k-sr-only">, {T.requiredHint}</span>
    </>
  );

  const renderField = (def) => {
    const id = `mq-${def.key}`;
    const errId = `${id}-err`;
    const err = errors[def.key];
    const common = {
      id,
      value: values[def.key] || '',
      onChange: set(def.key),
      className: err ? 'mq-err' : '',
      'aria-required': def.required ? true : undefined,
      'aria-invalid': err ? true : undefined,
      'aria-describedby': err ? errId : undefined,
      required: def.required
    };
    return (
      <div
        key={def.key}
        className={`mq-field ${def.wide ? 'mq-field-wide' : ''}`}
      >
        <label htmlFor={id} className="mq-label">
          {def.label}
          {def.required && requiredMark}
        </label>
        {def.kind === 'select' ? (
          <select {...common}>
            <option value="">{T.selectPlaceholder}</option>
            {def.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            {...common}
            type={INPUT_TYPE[def.kind || 'text']}
            inputMode={def.kind ? INPUT_MODE[def.kind] : undefined}
            autoComplete={def.autoComplete}
            placeholder={def.placeholder}
            maxLength={def.kind === 'year' ? 4 : undefined}
          />
        )}
        {err && (
          <span id={errId} className="mq-field-err">
            {T[err]}
          </span>
        )}
      </div>
    );
  };

  if (sent) {
    return (
      <div className="mq-card" ref={okRef}>
        <div className="mq-ok" role="status" aria-live="polite">
          <span className="mq-ok-mark" aria-hidden>
            ✓
          </span>
          <h3>{T.okTitle}</h3>
          <p>{T.okMsg}</p>
        </div>
      </div>
    );
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className="mq-card">
      <form className="mq-form" onSubmit={submit} noValidate>
        <fieldset className="mq-section">
          <legend className="mq-sec-title">{T.sec_contact}</legend>
          <div className="mq-choice">
            <span className="mq-label" id="mq-customerType-label">
              {T.customerType}
              {requiredMark}
            </span>
            <div
              className={`mq-segmented ${errors.customerType ? 'mq-seg-err' : ''}`}
              role="radiogroup"
              aria-labelledby="mq-customerType-label"
            >
              {CUSTOMER_TYPES.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={customerType === o.value}
                  className={`mq-seg-btn ${customerType === o.value ? 'mq-seg-active' : ''}`}
                  onClick={() => setCustomerType(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {errors.customerType && (
              <span className="mq-field-err">{T[errors.customerType]}</span>
            )}
          </div>
          <div className="mq-grid">{CONTACT_FIELDS.map(renderField)}</div>
          {customerType === 'company' && (
            <div className="mq-grid">{COMPANY_FIELDS.map(renderField)}</div>
          )}
        </fieldset>

        <fieldset className="mq-section">
          <legend className="mq-sec-title">{T.sec_vehicle}</legend>
          <p className="mq-sec-hint">{T.vehicleHint}</p>
          <div className="mq-grid">{VEHICLE_FIELDS.map(renderField)}</div>
        </fieldset>

        <fieldset className="mq-section">
          <legend className="mq-sec-title">{T.sec_cover}</legend>
          <div
            className="mq-choice"
            role="group"
            aria-labelledby="mq-cover-label"
          >
            <span className="mq-label" id="mq-cover-label">
              {T.coverLabel}
              {requiredMark}
            </span>
            <div className="mq-checks">
              {COVER_OPTIONS.map((o) => (
                <label
                  key={o.value}
                  className={`mq-check ${cover[o.value] ? 'mq-check-on' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={cover[o.value] === true}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCover((c) => ({ ...c, [o.value]: checked }));
                    }}
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
            {errors.cover ? (
              <span className="mq-field-err">{T[errors.cover]}</span>
            ) : (
              <span className="mq-hint">{T.coverHint}</span>
            )}
          </div>
          <div className="mq-grid">{COVER_FIELDS.map(renderField)}</div>
        </fieldset>

        <fieldset className="mq-section">
          <legend className="mq-sec-title">{T.sec_notes}</legend>
          <div className="mq-field">
            <label htmlFor="mq-notes" className="k-sr-only">
              {T.notes}
            </label>
            <textarea
              id="mq-notes"
              rows={4}
              value={values.notes || ''}
              onChange={set('notes')}
              placeholder={T.notesPlaceholder}
            />
          </div>
        </fieldset>

        <div className="mq-consents">
          <label
            className={`mq-consent ${errors.consent ? 'mq-consent-err' : ''}`}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              aria-required="true"
              aria-invalid={errors.consent ? true : undefined}
              required
            />
            <span>{T.consent}</span>
          </label>
        </div>

        {hasErrors && (
          <p className="mq-err-summary" role="alert">
            {T.errSummary}
          </p>
        )}
        {submitError && (
          <p className="mq-err-summary" role="alert">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          className="k-btn k-btn-primary k-btn-block"
          disabled={submitting}
          data-product-cta
        >
          {submitting ? T.submitting : T.submit}
          <span className="k-arrow" aria-hidden>
            →
          </span>
        </button>
      </form>
    </div>
  );
};

export default MotorQuoteForm;
